import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AssignmentDetail,
  AssignmentNote as AssignmentNoteType,
  BasicFacility,
  ConnectedFacilityPublic,
  PatchedAssignmentEventUpdate,
  PatchedAssignmentNote,
  PatchedAssignmentStatusUpdate,
  SourceTypeEnum,
  Status,
  AssignmentsService,
  ConnectionsService,
  DocumentsService,
  ShiftRateTypesEnum,
  PaginatedAssignmentNoteList,
  AssignmentNote,
  AssignmentEventHistory,
} from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { Avatar } from '../../../components/avatar';
import { Button } from '../../../components/button';
import { DOCUMENTS_QUERY_KEY, DocumentsTab } from '../../../components/documents-tab';
import { DrawerCloseButton } from '../../../components/drawer';
import { DropdownSelect } from '../../../components/dropdown-select';
import { HCO_NOTE_CHANNELS } from '../../../components/note-channel-select';
import { OrgManagedBanner } from '../../../components/org-managed-banner';
import { ProviderRecordLinks } from '../../../components/provider-record-links';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsButton, TabsContent } from '../../../components/tabs';
import { TooltippedText } from '../../../components/tooltipped-text';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { EventCodes, StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type {
  ActivityEventHistory,
  ActivityHistory,
  ActivityStatusHistory,
} from '../../../utils/activity-types';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { READ_ONLY_LABEL_STYLE, READ_ONLY_VALUE_STYLE } from '../../../utils/globalStyles';
import { getStatusTextStyle } from '../../../utils/statusStyles';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';
import { ExpensesTab } from './ExpensesTab';
import { ManagerPickerModal } from './ManagerPickerModal';
import {
  ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY,
  ASSIGNMENT_DOCUMENTS_COUNT_QUERY_KEY,
  ASSIGNMENT_EXPENSES_QUERY_KEY,
  ASSIGNMENT_NOTES_QUERY_KEY,
  ASSIGNMENT_QUERY_KEY,
  ASSIGNMENT_STATUS_HISTORY_QUERY_KEY,
  ASSIGNMENT_EVENT_HISTORY_QUERY_KEY,
  ASSIGNMENT_TIMECARDS_QUERY_KEY,
  ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY,
  STAFF_ASSIGNMENTS_LIST_QUERY_KEY,
} from './queryKeys';
import { ShiftsTab } from './ShiftsTab';

export type AssignmentDrawerProps = {
  assignmentId: string;
  onClose?: () => void;
  prevId?: string | null;
  nextId?: string | null;
  selectedQueueCode?: string | undefined;
  /** Hide the popout button (e.g. when already on the full-page route) */
  isFullPage?: boolean;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const AssignmentDrawer: React.FC<AssignmentDrawerProps> = ({
  assignmentId,
  onClose,
  prevId,
  nextId,
  selectedQueueCode,
  isFullPage = false,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const canEditAssignment = useHasPermission('update__assignments__assignment');
  const drawerRef = useRef<HTMLDivElement>(null);

  const defaultTab = 'shifts';
  const validTabs = ['shifts', 'expenses', 'documents'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : defaultTab
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      const next = new URLSearchParams(searchParams);
      if (tab && tab !== defaultTab) {
        next.set('tab', tab);
      } else {
        next.delete('tab');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [hasShiftDirty, setHasShiftDirty] = useState(false);
  const [targetStatusCode, setTargetStatusCode] = useState<string | null>(null);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);

  const { user: sessionUser, audienceIsStaff } = useContext(SessionContext);

  const LABEL_STYLE: string = READ_ONLY_LABEL_STYLE;
  const VALUE_STYLE: string = READ_ONLY_VALUE_STYLE;

  const { data: coiLabels = {} } = useCodeDefinitionsAsMap('COIParties');
  const { data: sourceTypeLabels = {} } = useCodeDefinitionsAsMap('SourceType');

  const { statuses: workflowStatuses, isLoading: isLoadingWorkflow } = useWorkflowMetadata(
    WorkflowScopes.Assignment
  );

  // Fetch assignment detail
  const {
    data: assignment,
    isLoading: isLoadingAssignment,
    error: assignmentError,
  } = useQuery<AssignmentDetail>({
    queryKey: [ASSIGNMENT_QUERY_KEY, assignmentId],
    queryFn: () => AssignmentsService.assignmentsAssignmentRetrieve({ id: assignmentId }),
    enabled: !!assignmentId,
    retry: false,
  });

  useDocumentTitle(assignment?.code ?? '', false, isFullPage && !!assignment?.code);

  // Fetch connected facilities for this provider (for shift edit modal)
  const effectiveProviderId = assignment?.provider?.id;
  const effectiveFacilityId = assignment?.facility?.id;
  const providerToken = (assignment?.provider as any)?.availability_token;

  const { data: connectedFacilitiesData } = useQuery({
    queryKey: ['assignmentFacilities', effectiveProviderId],
    queryFn: async () => {
      const response = await ConnectionsService.connectionsConnectedFacilityList({
        provider: effectiveProviderId,
        status: [StatusCodes.ACTIVE, StatusCodes.AWAIT_PRVLG],
        limit: 100,
      });
      return response.results.map((cf) => ({
        id: cf.facility.id,
        name: cf.facility.name,
        city: cf.facility.city,
        state: cf.facility.state,
      }));
    },
    enabled: !!effectiveProviderId && canEditAssignment,
  });

  const facilityOptions = useMemo(() => {
    if (!connectedFacilitiesData) return [];
    return connectedFacilitiesData
      .filter((f) => f.id)
      .map((f) => ({
        value: f.id!,
        label: f.name || 'Unnamed Facility',
        facility: f as BasicFacility,
      }));
  }, [connectedFacilitiesData]);

  // Fetch connected facility shift settings
  const { data: connectedFacility } = useQuery<ConnectedFacilityPublic>({
    queryKey: ['connectedFacilitySettings', effectiveFacilityId, providerToken],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityPublicLookupRetrieve({
        facility: effectiveFacilityId!,
        availabilityToken: providerToken!,
      }),
    enabled: !!effectiveFacilityId && !!providerToken && canEditAssignment,
  });

  const defaultConnectedFacility: ConnectedFacilityPublic = useMemo(
    () => ({
      shift_rate_types: [
        ShiftRateTypesEnum.DAY,
        ShiftRateTypesEnum.NIGHT,
        ShiftRateTypesEnum._24H,
        ShiftRateTypesEnum.CALL,
      ],
      shift_length: 12,
      shift_start_day: '07:00:00',
      shift_start_night: '19:00:00',
      shift_start_24h: '07:00:00',
    }),
    []
  );

  const effectiveConnectedFacility = connectedFacility || defaultConnectedFacility;

  // Fetch notes
  const { data: notesData } = useQuery({
    queryKey: [ASSIGNMENT_NOTES_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentNotesList({
        id: assignmentId!,
        ordering: '-created_at',
      }),
    enabled: !!assignmentId,
    select: (data: PaginatedAssignmentNoteList | AssignmentNote | AssignmentNote[]) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      if ('results' in data) {
        return data.results || [];
      }
      return [data as AssignmentNote];
    },
  });

  // Fetch status history
  const { data: statusHistory } = useQuery({
    queryKey: [ASSIGNMENT_STATUS_HISTORY_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentStatusHistoryRetrieve({
        id: assignmentId,
        sort: 'desc',
      }),
    enabled: !!assignmentId,
  });

  // Fetch change history
  const { data: changeHistory } = useQuery({
    queryKey: [ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentChangeHistoryRetrieve({
        id: assignmentId,
        sort: 'desc',
      }),
    enabled: !!assignmentId,
  });

  // Fetch event history
  const { data: eventHistoryData } = useQuery({
    queryKey: [ASSIGNMENT_EVENT_HISTORY_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentEventHistoryRetrieve({
        id: assignmentId!,
        sort: 'desc',
      }),
    enabled: !!assignmentId,
    select: (data: AssignmentEventHistory | AssignmentEventHistory[]) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [data];
    },
  });

  // Tab badge counts
  const { data: expensesData } = useQuery({
    queryKey: [ASSIGNMENT_EXPENSES_QUERY_KEY, assignmentId],
    queryFn: () => AssignmentsService.assignmentsAssignmentExpensesList({ id: assignmentId }),
    enabled: !!assignmentId,
  });
  const expenseCount = Array.isArray(expensesData) ? expensesData.length : 0;

  const { data: documentsData } = useQuery({
    queryKey: [ASSIGNMENT_DOCUMENTS_COUNT_QUERY_KEY, assignmentId],
    queryFn: () =>
      DocumentsService.documentsList({
        contextType: WorkflowScopes.Assignment,
        contextId: assignmentId,
        limit: 1,
      }),
    enabled: !!assignmentId,
  });
  const documentCount = (documentsData as any)?.count ?? 0;

  // Fetch timecards linked to this assignment (for the metadata cross-link)
  const { data: timecardsData } = useQuery({
    queryKey: [ASSIGNMENT_TIMECARDS_QUERY_KEY, assignmentId],
    queryFn: () =>
      AssignmentsService.assignmentsTimecardList({
        assignment: assignmentId,
        ordering: '-created_at',
      }),
    enabled: !!assignmentId,
  });
  const relatedTimecards = timecardsData?.results ?? [];

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_NOTES_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_STATUS_HISTORY_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_CHANGE_HISTORY_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_EVENT_HISTORY_QUERY_KEY, assignmentId],
    });
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_EXPENSES_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({
      queryKey: [ASSIGNMENT_DOCUMENTS_COUNT_QUERY_KEY, assignmentId],
    });
    // A status change can generate or lock the assignment's documents (the
    // ACL generated on PEND_CONFIRM), so refresh the Documents tab.
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_TIMECARDS_QUERY_KEY, assignmentId] });
    queryClient.invalidateQueries({ queryKey: [ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [STAFF_ASSIGNMENTS_LIST_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY] });
  }, [queryClient, assignmentId]);

  // Navigate to next/prev after item leaves queue
  const checkAndNavigateIfRemoved = useCallback(
    (updated?: AssignmentDetail) => {
      if (!selectedQueueCode || selectedQueueCode === 'all') return;
      const curStatusCode = updated?.current_status?.code ?? assignment?.current_status?.code;
      const queueName = getQueueNameForStatus(workflowStatuses, curStatusCode);

      if (queueName && queueName !== selectedQueueCode) {
        // Auto-advancing the drawer to the neighbouring item proved confusing — it
        // wasn't obvious the acted-on item had left the queue. Always close instead
        // so the user explicitly re-selects. nextId/prevId are kept behind this flag
        // in case we want to restore advancing later.
        const AUTO_ADVANCE_ON_REMOVE: boolean = false;
        const targetId = AUTO_ADVANCE_ON_REMOVE ? nextId || prevId || null : null;
        const nextParams = new URLSearchParams(searchParams);
        if (targetId) {
          nextParams.set('id', String(targetId));
        } else {
          nextParams.delete('id');
        }
        setSearchParams(nextParams, { replace: true });
        if (!targetId) onClose?.();
      }
    },
    [
      selectedQueueCode,
      assignment,
      workflowStatuses,
      nextId,
      prevId,
      searchParams,
      setSearchParams,
      onClose,
    ]
  );

  // A document upload/replace can change the assignment server-side (e.g.
  // confirming a signed ACL auto-confirms its assignment). Refetch the detail,
  // then — like a status change — navigate away / close the drawer if the new
  // status moves it out of the queue currently being viewed.
  const handleDocumentsChanged = useCallback(async () => {
    invalidateQueries();
    const updated = await queryClient.fetchQuery<AssignmentDetail>({
      queryKey: [ASSIGNMENT_QUERY_KEY, assignmentId],
      queryFn: () => AssignmentsService.assignmentsAssignmentRetrieve({ id: assignmentId }),
    });
    checkAndNavigateIfRemoved(updated);
  }, [invalidateQueries, queryClient, assignmentId, checkAndNavigateIfRemoved]);

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: (data: PatchedAssignmentStatusUpdate) =>
      AssignmentsService.assignmentsAssignmentStatusUpdatePartialUpdate({
        id: assignmentId,
        requestBody: data,
      }),
    onSuccess: (updated) => {
      toast.success('Status updated');
      invalidateQueries();
      checkAndNavigateIfRemoved(updated as AssignmentDetail);
    },
    onError: (error) => handleApiError(error),
  });

  // Event update mutation
  const eventUpdateMutation = useMutation({
    mutationFn: (data: { eventCode: string }) => {
      const requestBody: PatchedAssignmentEventUpdate = { code: data.eventCode };
      return AssignmentsService.assignmentsAssignmentEventUpdatePartialUpdate({
        id: assignmentId,
        requestBody,
      });
    },
    onSuccess: () => {
      toast.success('Event logged successfully');
      invalidateQueries();
    },
    onError: (error) => handleApiError(error),
  });

  const handleEventClick = async (eventCode: EventCodes) => {
    await eventUpdateMutation.mutateAsync({ eventCode });
  };

  const isPendConfirm = assignment?.current_status?.code === StatusCodes.PEND_CONFIRM;
  const isFacilityReview = assignment?.current_status?.code === StatusCodes.PEND_FAC_REV;

  const handleStatusUpdate = useCallback(
    (data: { statusCode: string | null; reasonCode: string | null; comment?: string }) => {
      statusUpdateMutation.mutate({
        code: data.statusCode || undefined,
        reason_code: data.reasonCode,
        comment: data.comment,
      });
      setIsStatusModalOpen(false);
    },
    [statusUpdateMutation]
  );

  const handleStatusClick = useCallback((status: Status) => {
    setTargetStatusCode(status.code);
    setIsStatusModalOpen(true);
  }, []);

  // Tab setup
  const tabs = useMemo(
    () => [
      { code: 'shifts', label: 'Shifts' },
      {
        code: 'expenses',
        label: expenseCount > 0 ? `Expenses (${expenseCount})` : 'Expenses',
      },
      {
        code: 'documents',
        label: documentCount > 0 ? `Documents (${documentCount})` : 'Documents',
      },
    ],
    [expenseCount, documentCount]
  );

  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      recheckOn: { id: assignmentId, tabs },
    });

  // Loading / error states
  if (isLoadingAssignment) {
    return <div className="tw:p-4 tw:text-center tw:text-zinc-500">Loading assignment...</div>;
  }

  if (assignmentError || !assignment) {
    return (
      <div className="tw:p-4">
        <div className="tw:flex tw:justify-end">
          <DrawerCloseButton onClick={() => onClose?.()} />
        </div>
        <div className="tw:text-center tw:text-stoplight-danger tw:bg-stoplight-danger/10 tw:my-2 tw:rounded-lg tw:py-4">
          Failed to load assignment.
        </div>
      </div>
    );
  }

  const currentStatusCode = assignment.current_status?.code ?? undefined;

  // Org-sourced (1099) assignments are owned by the organization's imported
  // data files — HCO users get no state- or pay-changing actions (status,
  // shifts, expenses), only documents, notes and the manager picker. Staff
  // keep their full access.
  const isOrgSourcedReadOnly =
    assignment.source_type === SourceTypeEnum.ORGANIZATION && !audienceIsStaff;
  const canManageAssignment = canEditAssignment && !isOrgSourcedReadOnly;

  return (
    <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-4">
      {/* Header */}
      <div className="tw:border-b tw:border-zinc-200 tw:pb-4">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <Avatar
            src={assignment.provider?.profile_picture || undefined}
            className="tw:size-12 tw:shrink-0 tw:rounded-lg"
            iconSize="2xl"
            square={true}
          />

          <div className="tw:flex-1 tw:min-w-0">
            <div className="tw:text-lg tw:font-semibold">{assignment.provider?.full_name}</div>
            <div className="tw:text-xs tw:text-zinc-500 tw:font-mono tw:flex tw:items-center tw:gap-1">
              {assignment.code}
              <TooltippedText
                tooltipContent="Copy code"
                tooltipClassName="tw:bg-brand-green-2 tw:text-zinc-700"
                placement="bottom"
              >
                <button
                  type="button"
                  className="tw:text-zinc-600 tw:hover:text-zinc-600 tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(assignment.code || '');
                    toast.success('Copied', { autoClose: 1500 });
                  }}
                >
                  <i className="far fa-copy tw:text-[10px]" />
                </button>
              </TooltippedText>
            </div>
          </div>

          {!isFullPage && !isMobile && (
            <button
              type="button"
              className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:p-1"
              onClick={() => {
                const tabParam = activeTab !== defaultTab ? `?tab=${activeTab}` : '';
                navigate(`/app/organization/assignments/${assignmentId}${tabParam}`, {
                  state: { referrer: window.location.pathname + window.location.search },
                });
              }}
              title="Open full page"
            >
              <i className="fas fa-up-right-from-square" />
            </button>
          )}
          <DrawerCloseButton onClick={() => onClose?.()} />
        </div>
        {/* Facility + location */}
        <div className="tw:mt-2 tw:text-sm tw:text-zinc-600 tw:flex tw:flex-wrap tw:gap-x-4">
          <span>
            <i className="fas fa-hospital tw:text-zinc-400 tw:mr-1" />
            {assignment.facility?.name}
          </span>
          <span>
            <i className="fas fa-map-marker-alt tw:text-zinc-400 tw:mr-1" />
            {getFacilityLocationDisplayValue(assignment.facility)}
          </span>
        </div>
        <div className="tw:flex tw:gap-2 tw:items-start tw:shrink-0 tw:mt-2 tw:flex-wrap">
          <StatusPill
            scope={WorkflowScopes.Assignment}
            status={assignment.current_status || 'Unknown'}
          />
          {assignment.current_reason?.title && (
            <div
              className={`tw:text-[12px] tw:mt-0.5 tw:text-right tw:max-w-[160px] tw:break-words ${getStatusTextStyle(assignment.current_status)}`}
            >
              {assignment.current_reason.title}
            </div>
          )}
          {assignment.provider?.id && (
            <>
              {/* Spacer (not ml-auto on the links): right-aligns the links on the
                  status line, but lets them start at the left once they wrap. */}
              <span className="tw:flex-1" aria-hidden="true" />
              {/* Provider profile + that provider's clearances/connections/
                  assignments/timecards lists (staff or org paths). */}
              <div data-testid="assignment-drawer-provider-links">
                <ProviderRecordLinks
                  providerId={assignment.provider.id}
                  searchName={assignment.provider.full_name}
                  isStaff={Boolean(audienceIsStaff)}
                  showProfileLink
                  referrerLabel={isFullPage ? assignment.code || 'Assignment' : 'Assignments'}
                />
              </div>
            </>
          )}
        </div>
        <div>
          <dl className="tw:grid tw:grid-cols-3 tw:gap-2 tw:text-sm tw:!pt-2 tw:!mt-2 tw:!mb-0 tw:border-t tw:border-zinc-200">
            <div>
              <dt className={LABEL_STYLE}>PLI Responsibility</dt>
              <dd className={VALUE_STYLE}>
                {coiLabels[assignment.responsible_for_coi || ''] ||
                  assignment.responsible_for_coi ||
                  '--'}
              </dd>
            </div>
            <div>
              <dt className={LABEL_STYLE}>Managed By</dt>
              <dd className={VALUE_STYLE}>
                <span className={assignment.managed_by ? undefined : 'tw:text-zinc-400 tw:italic'}>
                  {assignment.managed_by
                    ? `${assignment.managed_by.first_name || ''} ${
                        assignment.managed_by.last_name || ''
                      }`.trim() || '--'
                    : 'Unassigned'}
                </span>
                {canEditAssignment && (
                  <button
                    type="button"
                    onClick={() => setIsManagerModalOpen(true)}
                    aria-label="Change assignment manager"
                    className="tw:!ml-1 tw:!p-0 tw:bg-transparent tw:border-0 tw:text-brand-green-1 tw:cursor-pointer"
                  >
                    <i className="fa-solid fa-pencil tw:text-xs" />
                  </button>
                )}
              </dd>
            </div>
            <div>
              <dt className={LABEL_STYLE}>Source Type</dt>
              {assignment.source_type === SourceTypeEnum.ORGANIZATION ? (
                <span className={clsx('tw:ml-1', SOURCE_TYPE_ORGANIZATION_BADGE_STYLE)}>1099</span>
              ) : (
                sourceTypeLabels[assignment.source_type || ''] || assignment.source_type || '--'
              )}
            </div>
            {relatedTimecards.length > 0 && (
              <div>
                <dt className={LABEL_STYLE}>
                  {relatedTimecards.length > 1 ? 'Timecards' : 'Timecard'}
                </dt>
                <dd className={VALUE_STYLE}>
                  {relatedTimecards.map((tc, idx) => (
                    <React.Fragment key={tc.id}>
                      {idx > 0 && ', '}
                      <button
                        type="button"
                        className="tw:text-brand-green-0 tw:underline tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:!text-xs"
                        onClick={() =>
                          navigate(`/app/organization/timecards/${tc.id}`, {
                            state: {
                              referrer: window.location.pathname + window.location.search,
                            },
                          })
                        }
                      >
                        {tc.code}
                      </button>
                    </React.Fragment>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Status & Event action buttons */}
        <div className="tw:mt-3 tw:flex tw:flex-wrap tw:gap-2">
          {canEditAssignment && isPendConfirm && (
            <div className="tw:flex tw:flex-col tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:bg-amber-50 tw:p-3 tw:text-sm tw:text-amber-800">
              <div className="tw:flex tw:align-center tw:items-start tw:gap-1">
                <i
                  className="far fa-circle-info tw:text-base tw:pr-1 tw:pt-0.5"
                  aria-hidden="true"
                />
                Waiting on the signed Assignment Confirmation Letter. Once fully executed, the
                assignment will be confirmed.
              </div>
              {activeTab !== 'documents' && (
                <div className="">
                  <button
                    type="button"
                    className="tw:font-medium tw:text-amber-800 tw:underline tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer"
                    onClick={() => setActiveTab('documents')}
                  >
                    Go to Documents Tab
                  </button>
                </div>
              )}
            </div>
          )}
          {isOrgSourcedReadOnly && (
            <OrgManagedBanner message="This 1099 assignment is managed through your organization's imported data files and is view-only here. Documents and comments are still available." />
          )}
          {canManageAssignment && isFacilityReview && (
            <Button
              color="brandgreen0"
              onClick={() => handleEventClick(EventCodes.FAC_APPRV_SHIFTS)}
              disabled={eventUpdateMutation.isLoading}
              small={isMobile}
            >
              {eventUpdateMutation.isLoading ? 'Approving...' : 'Approve Shifts'}
            </Button>
          )}
          {canManageAssignment && (
            <StatusTransitionButtons
              isLoading={isLoadingWorkflow}
              currentStatusCode={currentStatusCode}
              workflowMetadata={workflowStatuses as StatusMetadata}
              onStatusUpdate={(status: Status) =>
                statusUpdateMutation.mutate({ code: status.code })
              }
              onStatusClick={handleStatusClick}
              isPending={statusUpdateMutation.isLoading}
              excludedStatusMap={{
                ...(hasShiftDirty ? { [StatusCodes.CANCELED]: true } : {}),
              }}
              small={isMobile}
            />
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs activeTab={activeTab} onChange={setActiveTab}>
        {isOverflowCollapsed ? (
          <div className="tw:pb-2 tw:px-1 tw:pt-1">
            <div className="tw:text-xs tw:text-zinc-500 tw:mb-1">Select View</div>
            <DropdownSelect
              inputId="assignment-drawer-section"
              aria-label="Assignment section"
              allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
              value={activeTab}
              isSearchable={false}
              isClearable={false}
              onChange={(opt: any) => {
                if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
              }}
            />
          </div>
        ) : (
          <div className="tw:border-b tw:border-zinc-200">
            <div
              ref={tabsMeasureRef as React.RefObject<HTMLDivElement>}
              className="tw:flex tw:gap-1 tw:overflow-hidden"
            >
              {tabs.map((tab) => (
                <TabsButton key={tab.code} tab={tab.code}>
                  {tab.label}
                </TabsButton>
              ))}
            </div>
          </div>
        )}

        <div className="tw:flex-1 tw:overflow-y-auto">
          <TabsContent tab="shifts">
            <ShiftsTab
              assignment={assignment}
              primaryFacilityName={assignment.facility?.name}
              canEdit={canManageAssignment}
              connectedFacility={effectiveConnectedFacility}
              facilityOptions={facilityOptions}
              providerToken={providerToken}
              onDirtyChange={setHasShiftDirty}
              onSuccess={() => {
                invalidateQueries();
              }}
            />
          </TabsContent>

          <TabsContent tab="expenses">
            <ExpensesTab
              assignment={assignment}
              canEdit={canManageAssignment}
              ownerId={assignment.healthcare_entity?.id || ''}
              relatedProviderId={assignment.provider?.id}
            />
          </TabsContent>

          <TabsContent tab="documents">
            <DocumentsTab
              contextType={WorkflowScopes.Assignment}
              contextId={assignmentId}
              ownerId={assignment.healthcare_entity.id}
              relatedProviderId={assignment.provider?.id}
              onDocumentsChanged={handleDocumentsChanged}
            />
          </TabsContent>
          <div className="tw:mt-6 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
            <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
            <ActivityLog
              enableCommentsFilter={true}
              enableStatusChangesFilter={true}
              enableUpdatesFilter={true}
              enableEventsFilter={true}
              composerPlaceholder={
                audienceIsStaff
                  ? 'Enter a comment...'
                  : 'Enter a comment (visible to all parties)...'
              }
              noteChannel={audienceIsStaff ? HCO_NOTE_CHANNELS : undefined}
              notes={notesData}
              statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
              changeHistory={changeHistory as ActivityHistory[] | undefined}
              eventHistory={eventHistoryData as ActivityEventHistory[] | undefined}
              noteMutations={{
                createNote: async (data) => {
                  await AssignmentsService.assignmentsAssignmentNotesCreate({
                    id: assignmentId,
                    requestBody: {
                      comment: data.comment,
                      channel: data.channel,
                      assignment: assignmentId,
                    } as AssignmentNoteType,
                  });
                },
                updateNote: async (noteId: string, data: PatchedAssignmentNote) => {
                  return AssignmentsService.assignmentsAssignmentNotePartialUpdate({
                    id: noteId,
                    requestBody: data,
                  });
                },
                deleteNote: async (noteId: string) => {
                  await AssignmentsService.assignmentsAssignmentNoteDestroy({
                    id: noteId,
                  });
                },
                notesQueryKey: [ASSIGNMENT_NOTES_QUERY_KEY, assignmentId],
                extraInvalidateQueryKeys: [[ASSIGNMENT_STATUS_HISTORY_QUERY_KEY, assignmentId]],
              }}
            />
          </div>
        </div>
      </Tabs>

      {/* Status transition modal */}
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onConfirm={handleStatusUpdate}
        initialStatusCode={currentStatusCode ?? null}
        targetStatusCode={targetStatusCode}
        workflowScope={WorkflowScopes.Assignment}
        isPending={statusUpdateMutation.isLoading}
        currentReasonCode={assignment.current_reason?.code ?? null}
        currentStatusTitle={assignment.current_status?.title ?? null}
      />

      {/* Manager picker modal */}
      {assignment.healthcare_entity?.id && (
        <ManagerPickerModal
          open={isManagerModalOpen}
          onClose={() => setIsManagerModalOpen(false)}
          assignmentId={assignmentId}
          healthcareEntityId={assignment.healthcare_entity.id}
          currentManagerId={assignment.managed_by?.id ?? null}
          currentUser={sessionUser}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_QUERY_KEY, assignmentId] });
            queryClient.invalidateQueries({ queryKey: [ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [STAFF_ASSIGNMENTS_LIST_QUERY_KEY] });
          }}
        />
      )}
    </div>
  );
};
